import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Lock, Check } from 'lucide-react';
import {
  DISPLAY_TYPES, DEFAULT_TYPES, RELATIVE_OPTIONS, getSlicerSetting, sortSlicerValues,
} from '@/lib/slicerSettings';

// Author dialog to configure a single slicer's look + default selection.
export function SlicerSettingsDialog({ open, onOpenChange, dimension, label, isDate, values = [], measures = [], visuals = [], pages = [], currentSettings, onSave }) {
  const [s, setS] = useState(getSlicerSetting({}, dimension));

  useEffect(() => {
    if (open) setS(getSlicerSetting({ [dimension]: currentSettings || {} }, dimension));
  }, [open, dimension, currentSettings]);

  if (!open) return null;
  const upd = (patch) => setS(prev => ({ ...prev, ...patch }));
  const updDefault = (patch) => setS(prev => ({ ...prev, defaultSelection: { ...prev.defaultSelection, ...patch } }));
  const multi = s.selectionMode === 'multiple';
  const sortedVals = sortSlicerValues(values, s.sortOrder);
  const defType = s.defaultSelection?.type || 'all';
  const availableDefaults = DEFAULT_TYPES.filter(d => !d.dateOnly || isDate);

  const toggleChosen = (v) => {
    const cur = s.defaultSelection?.values || [];
    const has = cur.includes(v);
    const next = has ? cur.filter(x => x !== v) : (multi ? [...cur, v] : [v]);
    updDefault({ values: next });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="slicer-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base">Slicer Settings — {label || dimension}</DialogTitle>
          <DialogDescription className="text-xs">Control how this filter looks and what it has selected when the dashboard opens.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Display type */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Display type</Label>
            <Select value={s.displayType || 'auto'} onValueChange={v => upd({ displayType: v === 'auto' ? null : v })}>
              <SelectTrigger className="h-9 text-sm rounded-lg" data-testid="slicer-set-displaytype"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Automatic</SelectItem>
                {DISPLAY_TYPES.filter(d => d.key !== 'calendar' || isDate).map(d => (
                  <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Selection mode + All option */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Selection mode</Label>
              <Select value={s.selectionMode} onValueChange={v => upd({ selectionMode: v })}>
                <SelectTrigger className="h-9 text-sm rounded-lg" data-testid="slicer-set-selectionmode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single selection</SelectItem>
                  <SelectItem value="multiple">Multiple selection</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Value sort order</Label>
              <Select value={s.sortOrder} onValueChange={v => upd({ sortOrder: v })}>
                <SelectTrigger className="h-9 text-sm rounded-lg" data-testid="slicer-set-sortorder"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="data">Data order</SelectItem>
                  <SelectItem value="asc">A → Z</SelectItem>
                  <SelectItem value="desc">Z → A</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border/30 px-3 py-2">
            <div>
              <Label className="text-xs font-semibold">Show "All" option</Label>
              <p className="text-[11px] text-muted-foreground">When off, the slicer always has a concrete value selected.</p>
            </div>
            <Switch checked={s.showAll} onCheckedChange={v => upd({ showAll: v })} data-testid="slicer-set-showall" />
          </div>

          {/* Default selection */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Default selection (when the dashboard opens)</Label>
            <Select value={defType} onValueChange={v => updDefault({ type: v })}>
              <SelectTrigger className="h-9 text-sm rounded-lg" data-testid="slicer-set-defaulttype"><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableDefaults.map(d => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>

            {defType === 'relative' && isDate && (
              <Select value={s.defaultSelection?.relativePreset || 'last_30_days'} onValueChange={v => updDefault({ relativePreset: v })}>
                <SelectTrigger className="h-9 text-sm rounded-lg mt-1" data-testid="slicer-set-relative"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RELATIVE_OPTIONS.map(o => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            )}

            {defType === 'top_measure' && (
              <Select value={s.defaultSelection?.measure || ''} onValueChange={v => updDefault({ measure: v })}>
                <SelectTrigger className="h-9 text-sm rounded-lg mt-1" data-testid="slicer-set-measure"><SelectValue placeholder="Choose a measure…" /></SelectTrigger>
                <SelectContent>
                  {measures.map(m => <SelectItem key={m.name || m} value={m.name || m}>{m.label || m.description || (m.name || m).replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            )}

            {defType === 'chosen' && (
              <div className="mt-1 max-h-40 overflow-auto rounded-lg border border-border/30 p-1.5 space-y-0.5" data-testid="slicer-set-chosen-list">
                {sortedVals.length === 0 && <p className="text-[11px] text-muted-foreground px-1 py-2">No values loaded yet for this field.</p>}
                {sortedVals.map(v => {
                  const sv = String(v);
                  const sel = (s.defaultSelection?.values || []).includes(sv);
                  return (
                    <button key={sv} type="button" onClick={() => toggleChosen(sv)} className={`w-full flex items-center gap-2 px-2 py-1 text-xs rounded ${sel ? 'bg-primary/15 text-primary' : 'hover:bg-muted/30'}`} data-testid={`slicer-set-chosen-${sv}`}>
                      {sel ? <Check className="w-3 h-3" /> : <span className="w-3" />}{sv}
                    </button>
                  );
                })}
                {!multi && <p className="text-[10px] text-muted-foreground px-1 pt-1">Single selection — only one value applies.</p>}
              </div>
            )}

            {defType === 'profile' && (
              <Input value={s.defaultSelection?.profileAttr || ''} onChange={e => updDefault({ profileAttr: e.target.value })} placeholder="Profile attribute (e.g. region)" className="h-9 text-sm rounded-lg mt-1" data-testid="slicer-set-profileattr" />
            )}
          </div>

          {/* Affects visuals (cross-filter scope) */}
          {visuals.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Affects visuals</Label>
              <p className="text-[11px] text-muted-foreground">Choose which visuals this slicer filters. All by default.</p>
              {pages.length > 1 && (
                <div className="flex flex-wrap gap-1 mb-1" data-testid="slicer-set-scope-pages">
                  {pages.map(pg => (
                    <button key={pg.id} type="button" onClick={() => upd({ scope: (pg.visualIds || []).length ? [...new Set(pg.visualIds)] : null })} className="px-2 py-0.5 text-[10px] rounded-full border border-border/40 hover:bg-muted/40" data-testid={`slicer-set-scope-page-${pg.id}`}>
                      All on {pg.name}
                    </button>
                  ))}
                </div>
              )}
              <div className="max-h-32 overflow-auto rounded-lg border border-border/30 p-1.5 space-y-0.5" data-testid="slicer-set-scope-list">
                {(() => {
                  const scopeAll = !s.scope || !s.scope.length;
                  return (
                    <>
                      <button type="button" onClick={() => upd({ scope: null })} className={`w-full flex items-center gap-2 px-2 py-1 text-xs rounded ${scopeAll ? 'bg-primary/15 text-primary' : 'hover:bg-muted/30'}`} data-testid="slicer-set-scope-all">
                        {scopeAll ? <Check className="w-3 h-3" /> : <span className="w-3" />}All visuals
                      </button>
                      {visuals.map(v => {
                        const on = !scopeAll && (s.scope || []).includes(v.id);
                        return (
                          <button key={v.id} type="button" onClick={() => {
                            const cur = scopeAll ? [] : [...(s.scope || [])];
                            const next = on ? cur.filter(x => x !== v.id) : [...cur, v.id];
                            upd({ scope: next.length ? next : null });
                          }} className={`w-full flex items-center gap-2 px-2 py-1 text-xs rounded ${on ? 'bg-primary/15 text-primary' : 'hover:bg-muted/30'}`} data-testid={`slicer-set-scope-${v.id}`}>
                            {on ? <Check className="w-3 h-3" /> : <span className="w-3" />}{v.title || v.id}
                          </button>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between rounded-lg border border-border/30 px-3 py-2">
            <div>
              <Label className="text-xs font-semibold">Sync across pages</Label>
              <p className="text-[11px] text-muted-foreground">Keep every slicer for this field in sync.</p>
            </div>
            <Switch checked={s.sync} onCheckedChange={v => upd({ sync: v })} data-testid="slicer-set-sync" />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border/30 px-3 py-2">
            <div>
              <Label className="text-xs font-semibold">Cascade from other slicers</Label>
              <p className="text-[11px] text-muted-foreground">Narrow this slicer's values by other slicers' current picks.</p>
            </div>
            <Switch checked={s.cascade !== false} onCheckedChange={v => upd({ cascade: v })} data-testid="slicer-set-cascade" />
          </div>

          {/* Custom title */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Custom title</Label>
            <Input value={s.customTitle || ''} onChange={e => upd({ customTitle: e.target.value })} placeholder={label || dimension} className="h-9 text-sm rounded-lg" data-testid="slicer-set-title" />
          </div>

          {/* Lock */}
          <div className="flex items-center justify-between rounded-lg border border-border/30 px-3 py-2">
            <div className="flex items-center gap-2">
              <Lock className="w-3.5 h-3.5 text-muted-foreground" />
              <div>
                <Label className="text-xs font-semibold">Lock for viewers</Label>
                <p className="text-[11px] text-muted-foreground">Viewers see the selection but can't change it.</p>
              </div>
            </div>
            <Switch checked={s.locked} onCheckedChange={v => upd({ locked: v })} data-testid="slicer-set-locked" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-lg" data-testid="slicer-set-cancel">Cancel</Button>
          <Button onClick={() => { onSave(dimension, s); onOpenChange(false); }} className="rounded-lg" data-testid="slicer-set-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
